import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../../layouts/Navbar";
import Footer from "../../layouts/Footer";
import Sidebar from "../../layouts/Sidebar";
import axios from "axios";
import { toast } from "react-toastify";

const UploadQuestion = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        title: "",
        category_id: "",
        sub_category_id: "",
        year: "",
        description: "",
        question_pdf: null,
        answer_pdf: null
    });
    
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [selectedFiles, setSelectedFiles] = useState({
        question_pdf: null,
        answer_pdf: null
    });

    // Generate years from 2000 to current year
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from(
        { length: currentYear - 2000 + 1 },
        (_, i) => (currentYear - i).toString()
    );

    // Fetch categories on component mount
    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await axios.get(
                "https://pastq.eddcodesgh.com/qApi/admin/categories/_viewcategories.php",
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
                    },
                    withCredentials: true,
                }
            );

            if (response.data.status === "success") {
                setCategories(response.data.records || []);
            } else {
                toast.error(response.data.message || "Failed to load categories");
            }
        } catch (error) {
            console.error("Categories fetch error:", error);
            toast.error("Network error loading categories");
        } finally {
            setLoadingCategories(false);
        }
    };

    // Fetch sub-categories when category is selected
    useEffect(() => {
        if (formData.category_id) {
            fetchSubCategories(formData.category_id);
        } else {
            setSubCategories([]);
        }
    }, [formData.category_id]);

    const fetchSubCategories = async (categoryId) => {
        try {
            const response = await axios.get(
                `https://pastq.eddcodesgh.com/qApi/admin/categories/subcategories.php?catId=${categoryId}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
                    },
                    withCredentials: true,
                }
            );

            if (response.data.status === "success") {
                setSubCategories(response.data.records || []);
            }
        } catch (error) {
            console.error("Subcategories fetch error:", error);
            setSubCategories([]);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // If category changes, clear sub_category_id
        if (name === "category_id") {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
                sub_category_id: ""
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        const file = files[0];
        
        if (file) {
            // Validate file type - strict PDF check
            const fileType = file.type.toLowerCase();
            const fileName = file.name.toLowerCase();
            const isPDF = fileType === 'application/pdf' || fileName.endsWith('.pdf');
            
            if (!isPDF) {
                toast.error("Only PDF files are allowed");
                e.target.value = ''; // Clear the input
                return;
            }
            
            // Validate file size (max 15MB)
            if (file.size > 15 * 1024 * 1024) {
                toast.error("File size should be less than 15MB");
                e.target.value = ''; // Clear the input
                return;
            }

            // Update form data with the actual File object
            setFormData((prev) => ({
                ...prev,
                [name]: file, // Store the actual File object
            }));

            setSelectedFiles((prev) => ({
                ...prev,
                [name]: {
                    name: file.name,
                    size: (file.size / 1024 / 1024).toFixed(2) + " MB",
                    type: file.type,
                },
            }));
            
            console.log(`File ${name} set:`, file.name, file.size, file.type);
        }
    };

    const removeFile = (fieldName) => {
        setFormData((prev) => ({
            ...prev,
            [fieldName]: null,
        }));

        setSelectedFiles((prev) => ({
            ...prev,
            [fieldName]: null,
        }));
    };

    const validateForm = () => {
        if (!formData.title.trim()) {
            toast.error("Question title is required");
            return false;
        }

        if (!formData.category_id) {
            toast.error("Please select a category");
            return false;
        }

        if (!formData.year) {
            toast.error("Please select a year");
            return false;
        }

        if (!formData.question_pdf) {
            toast.error("Question PDF is required");
            return false;
        }

        // Additional validation for the actual file
        if (formData.question_pdf instanceof File) {
            const fileType = formData.question_pdf.type.toLowerCase();
            const fileName = formData.question_pdf.name.toLowerCase();
            const isPDF = fileType === 'application/pdf' || fileName.endsWith('.pdf');
            
            if (!isPDF) {
                toast.error("Question file must be a PDF");
                return false;
            }
        }

        if (formData.answer_pdf instanceof File) {
            const fileType = formData.answer_pdf.type.toLowerCase();
            const fileName = formData.answer_pdf.name.toLowerCase();
            const isPDF = fileType === 'application/pdf' || fileName.endsWith('.pdf');
            
            if (!isPDF) {
                toast.error("Answer file must be a PDF");
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setLoading(true);
        setUploadProgress(0);

        try {
            // Create FormData object
            const formDataToSend = new FormData();
            
            // Append text fields
            formDataToSend.append("title", formData.title.trim());
            formDataToSend.append("category_id", formData.category_id);
            formDataToSend.append("year", formData.year);
            
            // Append description if exists
            if (formData.description && formData.description.trim()) {
                formDataToSend.append("description", formData.description.trim());
            }
            
            // Debug: Check files before appending
            console.log("Files to upload:");
            console.log("Question PDF:", formData.question_pdf);
            console.log("Answer PDF:", formData.answer_pdf);
            
            // Validate and append question PDF
            if (formData.question_pdf instanceof File) {
                // Validate file type
                const questionFile = formData.question_pdf;
                const isQuestionPDF = questionFile.type === 'application/pdf' || 
                                    questionFile.name.toLowerCase().endsWith('.pdf');
                
                if (!isQuestionPDF) {
                    toast.error("Question file must be a PDF");
                    setLoading(false);
                    return;
                }
                
                // Append the file
                formDataToSend.append("question_pdf", questionFile, questionFile.name);
            } else {
                toast.error("Question PDF is required");
                setLoading(false);
                return;
            }
            
            // Append answer PDF if exists
            if (formData.answer_pdf instanceof File) {
                const answerFile = formData.answer_pdf;
                const isAnswerPDF = answerFile.type === 'application/pdf' || 
                                answerFile.name.toLowerCase().endsWith('.pdf');
                
                if (!isAnswerPDF) {
                    toast.error("Answer file must be a PDF");
                    setLoading(false);
                    return;
                }
                
                formDataToSend.append("answer_pdf", answerFile, answerFile.name);
            }
            
            // Debug: Check FormData contents
            console.log("FormData entries:");
            for (let [key, value] of formDataToSend.entries()) {
                console.log(key, value instanceof File ? `${value.name} (${value.type})` : value);
            }

            // Log the FormData size
            console.log("FormData size:", formDataToSend.size);

            // Make the API call - Use localhost for development
            const response = await axios.post(
                "https://pastq.eddcodesgh.com/qApi/admin/questions/upload.php",
                formDataToSend,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        "Authorization": "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
                    },
                    timeout: 300000, // 5 minutes timeout for large files
                    onUploadProgress: (progressEvent) => {
                        if (progressEvent.total) {
                            const progress = Math.round(
                                (progressEvent.loaded * 100) / progressEvent.total
                            );
                            setUploadProgress(progress);
                        }
                    },
                }
            );

            console.log("Upload response:", response.data);

            if (response.data.status === 200 || response.data.status === "success") {
                toast.success(response.data.message || "Question uploaded successfully!");
                
                // Reset form
                setFormData({
                    title: "",
                    category_id: "",
                    sub_category_id: "",
                    year: "",
                    description: "",
                    question_pdf: null,
                    answer_pdf: null
                });
                setSelectedFiles({
                    question_pdf: null,
                    answer_pdf: null
                });
                setUploadProgress(0);
                
                // Navigate after success
                setTimeout(() => {
                    navigate("/admin/questions");
                }, 2000);
            } else {
                throw new Error(response.data.message || "Upload failed");
            }
        } catch (error) {
            console.error("Upload error details:", error);
            
            if (error.response) {
                // Server responded with error
                console.error("Response error:", error.response.data);
                const errorMessage = error.response.data.message || 
                                    error.response.data.error || 
                                    "Upload failed";
                toast.error(errorMessage);
                
                // Log detailed error information
                console.error("Status:", error.response.status);
                console.error("Headers:", error.response.headers);
            } else if (error.request) {
                // No response received
                console.error("No response:", error.request);
                toast.error("Network error. Please check your connection.");
            } else if (error.message.includes('timeout')) {
                toast.error("Upload timeout. The file might be too large.");
            } else {
                // Request setup error
                console.error("Request error:", error.message);
                toast.error(error.message || "An error occurred during upload");
            }
        } finally {
            setLoading(false);
        }
    };

    // Get selected category details
    const selectedCategory = categories.find(cat => cat.id == formData.category_id);

    return (
        <>
            <Navbar />
            <Sidebar />
           
            <main id="main" className="main">
                <div className="pagetitle">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h1>Upload Questions</h1>
                            <nav>
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/dashboard">Dashboard</Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <Link to="/admin/questions">Questions</Link>
                                    </li>
                                    <li className="breadcrumb-item active">Upload</li>
                                </ol>
                            </nav>
                        </div>
                        <Link to="/admin/questions" className="btn btn-outline-primary">
                            <i className="bi bi-arrow-left me-2"></i>
                            Back to Questions
                        </Link>
                    </div>
                </div>

                <section className="section">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card">
                                <div className="card-body">
                                    <h5 className="card-title">Upload New Questions</h5>
                                    <p className="text-muted mb-4">
                                        Upload questions via PDF files only.
                                    </p>

                                    <form onSubmit={handleSubmit}>
                                        {/* Basic Information */}
                                        <div className="row mb-4">
                                            <div className="col-md-8">
                                                <label className="form-label">
                                                    Question Title <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="title"
                                                    className="form-control"
                                                    placeholder="e.g., 2024 WASSCE Mathematics Paper 2"
                                                    value={formData.title}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                                <div className="form-text">
                                                    Enter a descriptive title for this question set
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label">
                                                    Year <span className="text-danger">*</span>
                                                </label>
                                                <select
                                                    name="year"
                                                    className="form-select"
                                                    value={formData.year}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="">Select Year</option>
                                                    {yearOptions.map((year) => (
                                                        <option key={year} value={year}>
                                                            {year}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        {/* Category Selection */}
                                        <div className="row mb-4">
                                            <div className="col-md-6">
                                                <label className="form-label">
                                                    Exam Type <span className="text-danger">*</span>
                                                </label>
                                                <select
                                                    name="category_id"
                                                    className="form-select"
                                                    value={formData.category_id}
                                                    onChange={handleInputChange}
                                                    required
                                                    disabled={loadingCategories}
                                                >
                                                    <option value="">Select Category</option>
                                                    {categories.map((category) => (
                                                        <option key={category.id} value={category.id}>
                                                            {category.title}
                                                            {category.years && category.years.length > 0 && 
                                                                ` (Years: ${category.years.join(', ')})`
                                                            }
                                                        </option>
                                                    ))}
                                                </select>
                                                {loadingCategories && (
                                                    <div className="form-text">
                                                        <small>Loading categories...</small>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label">
                                                    Sub-category (Optional)
                                                </label>
                                                <select
                                                    name="sub_category_id"
                                                    className="form-select"
                                                    value={formData.sub_category_id}
                                                    onChange={handleInputChange}
                                                    disabled={!formData.category_id || subCategories.length === 0}
                                                >
                                                    <option value="">Select Sub-category</option>
                                                    {subCategories.map((sub) => (
                                                        <option key={sub.id} value={sub.id}>
                                                            {sub.year}
                                                            {sub.question_count > 0 && ` (${sub.question_count} questions)`}
                                                        </option>
                                                    ))}
                                                </select>
                                                <div className="form-text">
                                                    <small>
                                                        Select existing sub-category or leave empty to create new
                                                    </small>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Description */}
                                        <div className="mb-4">
                                            <label className="form-label">
                                                Description (Optional)
                                            </label>
                                            <textarea
                                                name="description"
                                                className="form-control"
                                                rows="3"
                                                placeholder="Add any additional notes or instructions..."
                                                value={formData.description}
                                                onChange={handleInputChange}
                                            ></textarea>
                                        </div>

                                        {/* PDF Upload Section */}
                                        <div className="mb-4">
                                            <div className="row">
                                                {/* Question PDF Upload */}
                                                <div className="col-md-6 mb-4">
                                                    <div className="card border-primary">
                                                        <div className="card-body">
                                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                                <h6 className="card-title mb-0 text-primary">
                                                                    <i className="bi bi-file-earmark-text me-2"></i>
                                                                    Question PDF <span className="text-danger">*</span>
                                                                </h6>
                                                            </div>
                                                            
                                                            <div className="file-upload-area">
                                                                {!selectedFiles.question_pdf ? (
                                                                    <div className="file-dropzone text-center p-5 border rounded-3 bg-light">
                                                                        <i className="bi bi-cloud-upload display-4 text-muted mb-3"></i>
                                                                        <p className="mb-2">Drag & drop question PDF here</p>
                                                                        <p className="text-muted small mb-3">or click to browse</p>
                                                                        <input
                                                                            type="file"
                                                                            name="question_pdf"
                                                                            className="form-control d-none"
                                                                            id="questionPdf"
                                                                            accept=".pdf,application/pdf"
                                                                            onChange={handleFileChange}
                                                                            required
                                                                        />
                                                                        <label htmlFor="questionPdf" className="btn btn-primary">
                                                                            <i className="bi bi-upload me-2"></i>
                                                                            Choose File
                                                                        </label>
                                                                    </div>
                                                                ) : (
                                                                    <div className="file-preview p-3 border rounded-3 bg-success bg-opacity-10">
                                                                        <div className="d-flex justify-content-between align-items-center">
                                                                            <div>
                                                                                <i className="bi bi-file-pdf text-danger fs-4"></i>
                                                                                <div className="ms-3 d-inline-block">
                                                                                    <strong>{selectedFiles.question_pdf.name}</strong>
                                                                                    <div className="text-muted small">
                                                                                        {selectedFiles.question_pdf.size}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-outline-danger"
                                                                                onClick={() => removeFile("question_pdf")}
                                                                            >
                                                                                <i className="bi bi-trash"></i>
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                <div className="form-text mt-2">
                                                                    Upload the question paper in PDF format (max 15MB)
                                                                </div>
                                                                {selectedFiles.question_pdf && (
                                                                    <div className="alert alert-success alert-sm mt-2 p-2">
                                                                        <i className="bi bi-check-circle me-2"></i>
                                                                        File selected: {selectedFiles.question_pdf.name}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Answer PDF Upload */}
                                                <div className="col-md-6 mb-4">
                                                    <div className="card border-success">
                                                        <div className="card-body">
                                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                                <h6 className="card-title mb-0 text-success">
                                                                    <i className="bi bi-file-earmark-check me-2"></i>
                                                                    Answer PDF (Optional)
                                                                </h6>
                                                            </div>
                                                            
                                                            <div className="file-upload-area">
                                                                {!selectedFiles.answer_pdf ? (
                                                                    <div className="file-dropzone text-center p-5 border rounded-3 bg-light">
                                                                        <i className="bi bi-cloud-upload display-4 text-muted mb-3"></i>
                                                                        <p className="mb-2">Drag & drop answer PDF here</p>
                                                                        <p className="text-muted small mb-3">or click to browse</p>
                                                                        <input
                                                                            type="file"
                                                                            name="answer_pdf"
                                                                            className="form-control d-none"
                                                                            id="answerPdf"
                                                                            accept=".pdf,application/pdf"
                                                                            onChange={handleFileChange}
                                                                        />
                                                                        <label htmlFor="answerPdf" className="btn btn-success">
                                                                            <i className="bi bi-upload me-2"></i>
                                                                            Choose File
                                                                        </label>
                                                                    </div>
                                                                ) : (
                                                                    <div className="file-preview p-3 border rounded-3 bg-success bg-opacity-10">
                                                                        <div className="d-flex justify-content-between align-items-center">
                                                                            <div>
                                                                                <i className="bi bi-file-pdf text-success fs-4"></i>
                                                                                <div className="ms-3 d-inline-block">
                                                                                    <strong>{selectedFiles.answer_pdf.name}</strong>
                                                                                    <div className="text-muted small">
                                                                                        {selectedFiles.answer_pdf.size}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-outline-danger"
                                                                                onClick={() => removeFile("answer_pdf")}
                                                                            >
                                                                                <i className="bi bi-trash"></i>
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                <div className="form-text mt-2">
                                                                    Upload the answer sheet in PDF format (max 15MB)
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Debug Info - Remove in production */}
                                        <div className="mb-4 p-3 border rounded bg-light">
                                            <h6 className="text-muted">Debug Information:</h6>
                                            <small>
                                                Question File: {formData.question_pdf ? "✓ Selected" : "✗ Not selected"}<br/>
                                                Answer File: {formData.answer_pdf ? "✓ Selected" : "✗ Not selected"}<br/>
                                                Category: {formData.category_id}<br/>
                                                Year: {formData.year}
                                            </small>
                                        </div>

                                        {/* Upload Progress */}
                                        {uploadProgress > 0 && (
                                            <div className="mb-4">
                                                <div className="progress" style={{ height: "10px" }}>
                                                    <div
                                                        className="progress-bar progress-bar-striped progress-bar-animated"
                                                        role="progressbar"
                                                        style={{ width: `${uploadProgress}%` }}
                                                        aria-valuenow={uploadProgress}
                                                        aria-valuemin="0"
                                                        aria-valuemax="100"
                                                    >
                                                        {uploadProgress}%
                                                    </div>
                                                </div>
                                                <div className="text-center mt-2">
                                                    <small className="text-muted">
                                                        Uploading... {uploadProgress}% complete
                                                    </small>
                                                </div>
                                            </div>
                                        )}

                                        {/* Submit Button */}
                                        <div className="d-flex justify-content-between mt-4">
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={() => navigate("/admin/questions")}
                                                disabled={loading}
                                            >
                                                <i className="bi bi-x-circle me-2"></i>
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="btn btn-primary px-5"
                                                disabled={loading || !formData.question_pdf}
                                            >
                                                {loading ? (
                                                    <>
                                                        <span
                                                            className="spinner-border spinner-border-sm me-2"
                                                            role="status"
                                                            aria-hidden="true"
                                                        ></span>
                                                        Uploading...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bi bi-cloud-upload me-2"></i>
                                                        Upload Questions
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>

                            {/* Instructions Card */}
                            <div className="card mt-4">
                                <div className="card-body">
                                    <h6 className="card-title">
                                        <i className="bi bi-info-circle me-2 text-info"></i>
                                        Upload Instructions
                                    </h6>
                                    <ul className="list-unstyled mb-0">
                                        <li className="mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Supported file format: PDF only
                                        </li>
                                        <li className="mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Maximum file size: 15MB per file
                                        </li>
                                        <li className="mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Use clear titles for easy searching
                                        </li>
                                        <li className="mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Select appropriate category and year
                                        </li>
                                        <li className="mb-2">
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Question PDF is required, Answer PDF is optional
                                        </li>
                                        <li>
                                            <i className="bi bi-check-circle text-success me-2"></i>
                                            Only .pdf files are accepted
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />

            <style jsx>{`
                .card {
                    border-radius: 12px;
                    border: 1px solid rgba(0, 0, 0, 0.08);
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
                }
                
                .file-dropzone {
                    border: 2px dashed #dee2e6;
                    transition: all 0.3s ease;
                    cursor: pointer;
                }
                
                .file-dropzone:hover {
                    border-color: #0d6efd;
                    background-color: rgba(13, 110, 253, 0.05);
                }
                
                .file-preview {
                    border: 2px solid #198754;
                    animation: fadeIn 0.3s ease;
                }
                
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                
                .progress {
                    overflow: visible;
                }
                
                .progress-bar {
                    border-radius: 4px;
                    position: relative;
                }
                
                .progress-bar::after {
                    content: '';
                    position: absolute;
                    right: -8px;
                    top: -3px;
                    width: 16px;
                    height: 16px;
                    background: #fff;
                    border: 2px solid #0d6efd;
                    border-radius: 50%;
                }
                
                @media (max-width: 768px) {
                    .file-dropzone {
                        padding: 2rem 1rem !important;
                    }
                }
            `}</style>
        </>
    );
};

export default UploadQuestion;